import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { hasPermission } from '@/composables/usePermission'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import { EventBus } from '@/views/habit/EventBus.js'

export default function useGapList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, getItemsAggregate } = useLambda()
  const { client } = store.state?.userStore?.userData

  const refGapTable = ref(null)
  const gaps = ref([])
  const perPage = ref(10)
  const totalGaps = ref(0)
  const currentPage = ref(1)
  const searchQuery = ref('')
  const sortBy = ref('obligation.order')
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)
  const collection = 'gap'

  // Filters
  const gapStatusFilter = ref(null)
  const responsibleFilter = ref([])
  const obligationFilter = ref(null)
  const overdueGapsFilter = ref(false)

  const clearFiltersDisabled = computed(() =>
    !searchQuery.value &&
    !gapStatusFilter.value &&
    !responsibleFilter.value.length &&
    !obligationFilter.value &&
    !overdueGapsFilter.value
  )

  // Update filters from Filters component
  const updateFilter = (data) => {
    gapStatusFilter.value = data.gapStatusFilter
    responsibleFilter.value = data.workerFilter
    obligationFilter.value = data.obligationFilter
    overdueGapsFilter.value = data.overdueGapsFilter

    checkPageAndFetch()
  }

  // Calculate compliance progress
  const complianceProgress = computed(() => {
    if (!gaps.value || gaps.value.length === 0) return 0

    const applicableGaps = gaps.value.filter(g => g.status !== 'No aplica')
    if (applicableGaps.length === 0) return 0

    const implemented = applicableGaps.filter(g => g.status === 'Implementado').length
    return Math.round((implemented / applicableGaps.length) * 100)
  })

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      {
        key: 'actions',
        label: i18n.t('message.tableHeader.actions'),
      },
      {
        key: 'obligationOrder',
        label: 'N°',
        sortable: true,
      },
      {
        key: 'obligationName',
        label: 'Obligación',
        sortable: true,
      },
      {
        key: 'obligationArticle',
        label: 'Artículo',
        sortable: true,
      },
      {
        key: 'status',
        label: 'Estado',
        sortable: true,
      },
      {
        key: 'responsibleName',
        label: 'Responsable',
        sortable: true,
      },
      {
        key: 'deadline',
        label: 'Plazo',
        sortable: true,
      },
      {
        key: 'planStatus',
        label: 'Estado Plan',
        sortable: true,
      },
    ]
  })

  const fetchGaps = async () => {
    isLoading.value = true

    try {
      // Build query
      const query = {
        client_id: oid(client.$oid),
        deleted: { $ne: true }
      }

      // Apply filters
      if (gapStatusFilter.value) {
        query.status = gapStatusFilter.value
      }
      if (responsibleFilter.value.length) {
        query.responsible_id = { $in: responsibleFilter.value.map(e => oid(e)) }
      }
      if (obligationFilter.value) {
        query.obligation_id = oid(obligationFilter.value)
      }
      if (overdueGapsFilter.value) {
        // Filter for overdue gaps: status = Parcial or No implementado AND deadline < today
        query.$and = [
          { status: { $in: ['Parcial', 'No implementado'] } },
          { deadline: { $lt: date(new Date()) } }
        ]
      }

      // Clean null values
      Object.keys(query).forEach(key => {
        if (query[key] === null) delete query[key]
      })

      const pipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'gapObligation',
            localField: 'obligation_id',
            foreignField: '_id',
            as: 'obligation',
            pipeline: [
              {
                $project: {
                  name: 1,
                  article: 1,
                  description: 1,
                  order: 1,
                }
              }
            ]
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'responsible_id',
            foreignField: '_id',
            as: 'responsible',
            pipeline: [
              {
                $project: {
                  name: 1,
                  lastname: 1,
                }
              }
            ]
          }
        },
        {
          $lookup: {
            from: 'company',
            localField: 'empresa_id',
            foreignField: '_id',
            as: 'empresa',
            pipeline: [{ $project: { name: 1 } }]
          }
        },
        {
          $addFields: {
            obligation: { $arrayElemAt: ['$obligation', 0] },
            responsible: { $arrayElemAt: ['$responsible', 0] },
            empresa: { $arrayElemAt: ['$empresa', 0] },
            obligationName: { $arrayElemAt: ['$obligation.name', 0] },
            obligationArticle: { $arrayElemAt: ['$obligation.article', 0] },
            obligationOrder: { $arrayElemAt: ['$obligation.order', 0] },
            responsibleName: {
              $concat: [
                { $ifNull: [{ $arrayElemAt: ['$responsible.name', 0] }, ''] },
                ' ',
                { $ifNull: [{ $arrayElemAt: ['$responsible.lastname', 0] }, ''] }
              ]
            },
          }
        }
      ]

      // Search in obligation name and article
      if (searchQuery.value) {
        pipeline.push({
          $match: {
            $or: [
              { obligationName: { $regex: searchQuery.value, $options: 'i' } },
              { obligationArticle: { $regex: searchQuery.value, $options: 'i' } },
              { responsibleName: { $regex: searchQuery.value, $options: 'i' } }
            ]
          }
        })
      }

      pipeline.push(
        { $sort: { obligationOrder: isSortDirDesc.value ? -1 : 1 } },
        { $skip: (currentPage.value - 1) * perPage.value },
        { $limit: perPage.value }
      )

      const countPipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'gapObligation',
            localField: 'obligation_id',
            foreignField: '_id',
            as: 'obligation',
            pipeline: [
              {
                $project: {
                  name: 1,
                  article: 1,
                }
              }
            ]
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'responsible_id',
            foreignField: '_id',
            as: 'responsible',
            pipeline: [
              {
                $project: {
                  name: 1,
                  lastname: 1,
                }
              }
            ]
          }
        },
        {
          $addFields: {
            obligation: { $arrayElemAt: ['$obligation', 0] },
            responsible: { $arrayElemAt: ['$responsible', 0] },
            obligationName: { $arrayElemAt: ['$obligation.name', 0] },
            obligationArticle: { $arrayElemAt: ['$obligation.article', 0] },
            responsibleName: {
              $concat: [
                { $ifNull: [{ $arrayElemAt: ['$responsible.name', 0] }, ''] },
                ' ',
                { $ifNull: [{ $arrayElemAt: ['$responsible.lastname', 0] }, ''] }
              ]
            },
          }
        }
      ]

      // Add search to count pipeline
      if (searchQuery.value) {
        countPipeline.push({
          $match: {
            $or: [
              { obligationName: { $regex: searchQuery.value, $options: 'i' } },
              { obligationArticle: { $regex: searchQuery.value, $options: 'i' } },
              { responsibleName: { $regex: searchQuery.value, $options: 'i' } }
            ]
          }
        })
      }

      countPipeline.push({ $count: 'total' })

      const result = await getItemsAggregate({ collection, pipeline })
      const countResult = await getItemsAggregate({ collection, pipeline: countPipeline })

      gaps.value = result || []
      totalGaps.value = countResult?.[0]?.total || 0
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: 'Error al cargar brechas' })
    } finally {
      isLoading.value = false
    }
  }

  const deleteGap = async (gap) => {
    if (!hasPermission('privacy-gap', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      const query = { _id: oid(gap._id), client_id: oid(client.$oid) }

      const payload = { deleted: true }
      const action = { $set: payload }
      await updateItem({ collection, query, action })

      showSuccessMessage('Brecha eliminada correctamente')
      fetchGaps()
    } catch (error) {
      console.log(error)
      showErrorMessage('Error al eliminar brecha')
    }
  }

  const clearFilters = () => {
    searchQuery.value = ''
    gapStatusFilter.value = null
    responsibleFilter.value = []
    obligationFilter.value = null
    overdueGapsFilter.value = false

    // Emit event to reset filters in Filters component
    EventBus.$emit('resetFilters')

    checkPageAndFetch()
  }

  const checkPageAndFetch = () => {
    if (currentPage.value === 1) fetchGaps()
    else currentPage.value = 1
  }

  // Fetch all gaps for export (respecting filters, no pagination)
  const fetchAllGapsForExport = async () => {
    try {
      // Build query same as fetchGaps
      const query = {
        client_id: oid(client.$oid),
        deleted: { $ne: true }
      }

      // Apply filters
      if (gapStatusFilter.value) {
        query.status = gapStatusFilter.value
      }
      if (responsibleFilter.value.length) {
        query.responsible_id = { $in: responsibleFilter.value.map(e => oid(e)) }
      }
      if (obligationFilter.value) {
        query.obligation_id = oid(obligationFilter.value)
      }
      if (overdueGapsFilter.value) {
        // Filter for overdue gaps: status = Parcial or No implementado AND deadline < today
        query.$and = [
          { status: { $in: ['Parcial', 'No implementado'] } },
          { deadline: { $lt: new Date() } }
        ]
      }

      // Clean null values
      Object.keys(query).forEach(key => {
        if (query[key] === null) delete query[key]
      })

      const pipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'gapObligation',
            localField: 'obligation_id',
            foreignField: '_id',
            as: 'obligation',
            pipeline: [
              {
                $project: {
                  name: 1,
                  article: 1,
                  description: 1,
                  order: 1,
                }
              }
            ]
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'responsible_id',
            foreignField: '_id',
            as: 'responsible',
            pipeline: [
              {
                $project: {
                  name: 1,
                  lastname: 1,
                }
              }
            ]
          }
        },
        {
          $lookup: {
            from: 'company',
            localField: 'empresa_id',
            foreignField: '_id',
            as: 'empresa',
            pipeline: [{ $project: { name: 1 } }]
          }
        },
        {
          $addFields: {
            obligation: { $arrayElemAt: ['$obligation', 0] },
            responsible: { $arrayElemAt: ['$responsible', 0] },
            empresa: { $arrayElemAt: ['$empresa', 0] },
            obligationName: { $arrayElemAt: ['$obligation.name', 0] },
            obligationArticle: { $arrayElemAt: ['$obligation.article', 0] },
            obligationOrder: { $arrayElemAt: ['$obligation.order', 0] },
            responsibleName: {
              $concat: [
                { $ifNull: [{ $arrayElemAt: ['$responsible.name', 0] }, ''] },
                ' ',
                { $ifNull: [{ $arrayElemAt: ['$responsible.lastname', 0] }, ''] }
              ]
            },
          }
        }
      ]

      // Search in obligation name and article
      if (searchQuery.value) {
        pipeline.push({
          $match: {
            $or: [
              { obligationName: { $regex: searchQuery.value, $options: 'i' } },
              { obligationArticle: { $regex: searchQuery.value, $options: 'i' } },
              { responsibleName: { $regex: searchQuery.value, $options: 'i' } }
            ]
          }
        })
      }

      pipeline.push({ $sort: { obligationOrder: 1 } })

      const result = await getItemsAggregate({ collection, pipeline })

      return result || []
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: 'Error al cargar brechas para exportar' })
      return []
    }
  }

  watch([currentPage], () => {
    fetchGaps()
  })

  watch([searchQuery, sortBy, isSortDirDesc, perPage], () => {
    checkPageAndFetch()
  })


  return {
    fetchGaps,
    tableColumns,
    perPage,
    currentPage,
    totalGaps,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refGapTable,
    gaps,
    deleteGap,
    isLoading,
    clearFilters,
    clearFiltersDisabled,
    updateFilter,
    fetchAllGapsForExport,
    complianceProgress,
  }
}
